import * as Sentry from "@sentry/react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { HotelOverview } from "@/features/hotels/components/hotel/HotelOverview";
import { HotelSearchHeader } from "@/features/hotels/components/hotel/HotelSearchHeader";
import { PaymentOptions } from "@/features/hotels/components/PaymentOptions";
import { useHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";

export const Route = createFileRoute(
  "/_authenticated/hotels/_hotels/_hotel-offer/$id/details",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

/**
 * First step of the hotel booking process.
 */
function RouteComponent() {
  const intl = useIntl();
  const navigate = Route.useNavigate();

  const { data: hotel } = useHotelDataContext();

  if (!hotel) {
    // If the app fails to load the hotel data or to pass search data, redirect to the hotel list page
    // This is a fallback in case the user tries to access this page directly
    // It's unlikely that this will happen, but it's a good fallback to have
    // Avoids the need to handle these cases in the other components
    Sentry.captureMessage(
      "User tried to access hotel details page directly without going through the hotel offer page",
      "fatal",
    );
    return <Navigate to="/hotels/list" />;
  }

  const originalTotalPrice = hotel.minPrice?.value || 0;

  return (
    <>
      <div className="mb-4 px-main">
        <HotelSearchHeader />
      </div>
      <div className="px-main">
        <HotelOverview hotel={hotel} />
      </div>
      <PersistentDrawer>
        <PaymentOptions totalPrice={originalTotalPrice} />

        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            navigate({ to: "/hotels/$id/rooms" });
          }}
        >
          {intl.formatMessage({
            defaultMessage: "Choose a Room",
            description:
              "Page or section heading prompting the user to choose a room type",
          })}
        </Button>
      </PersistentDrawer>
    </>
  );
}
